import { type Database } from "@fitness-app/supabase";

import { type AutomationStartPoint } from "./Automation";
import { type AppLanguage } from "./AppLanguage";
import { type WeightHint } from "./TrainingProgram";

export type TraineeSettingsEntity = Database["public"]["Tables"]["trainee_settings"]["Row"];

export enum NotificationsFrequency {
  DAILY = "DAILY",
  ONCE_A_WEEK = "ONCE_A_WEEK",
  ONCE_A_TWO_WEEKS = "ONCE_A_TWO_WEEKS",
  ONCE_A_MONTH = "ONCE_A_MONTH",
}

export interface NotificationsSettings {
  allowNotifications: null | boolean;
  hideReadReminders: boolean;
  activeWeightMeasurementReminder: boolean;
  activeBodyMeasurementReminder: boolean;
  activeWorkoutsReminder: boolean;
  /** TRE-2298 — Sunday push with the weekly summary; missing means on (opt-out). */
  activeWeeklySummary?: boolean;
  enableTimerSound: boolean;
  weightMeasurementFrequency: NotificationsFrequency;
  bodyMeasurementFrequency: NotificationsFrequency;
}

export interface AvailableOffer {
  images: string[];
  name: string;
  customUrl?: string;
  customButtonLabel?: string;
  description: string;
  id: string;
  features: string[];
  productId: string;
}

export interface TraineeOnboarding {
  isOnboardingCompleted: boolean;
  shouldRedirectToInitialSurvey: boolean;
  surveyId?: string | null;
}

export interface UpsellTo {
  productId: string;
  productName: string;
  checkoutId: string | null;
  presaleUrl?: string;
  linkLabel?: string;
}

export interface TraineeConfig {
  products?: { id: string; name: string }[];
  upsellTo?: UpsellTo[];
  /** Kill switch for Sentry mobile session replay (ANR mitigation, see issue 6913026960). */
  disableSessionReplay?: boolean;
  /**
   * Master kill switch for ALL client-side observability overhead: Sentry session/error replay,
   * performance tracing, profiling, Trace Metrics, logs and our custom instrumentation. Crash/error
   * reporting stays on. Read synchronously at `Sentry.init` from an MMKV cache, so it takes full
   * effect from the next launch (the current session is downgraded at runtime where possible).
   */
  disableObservability?: boolean;
}

export interface OngoingAutomation {
  name: string;
  id: string;
  startAutomationFrom: AutomationStartPoint;
  productId: string;
}

export interface WorkoutSettings {
  weightHint?: WeightHint;
  /**
   * Whether the `weightHint` prefill also covers the repeat count, on top of weight and duration.
   * Only has an effect when `weightHint` is something other than `DEFAULT`. Defaults to `true`
   * (see `getWorkoutHintSettings`). Turning it off keeps the trainer's planned repeats in the set
   * fields, so the workout list and the exercise sheet always show the same number (TRE-1824/1825).
   */
  applyHintToRepeats?: boolean;
  autoExportWorkoutsToHealth?: boolean;
  /** iOS: show the running workout as a Live Activity on the lock screen. Defaults to `true`. */
  showWorkoutLiveActivity?: boolean;
  /**
   * iOS: the Apple Watch app measures heart rate and active energy during a workout, saves it to
   * Health and links it to the workout day. Replaces the phone's own Health export when a watch
   * is paired. Defaults to `true` (TRE-2244).
   */
  recordWatchWorkouts?: boolean;
}

export interface TraineeSettings extends Omit<
  TraineeSettingsEntity,
  | "notificationsSettings"
  | "availableOffers"
  | "onboarding"
  | "config"
  | "ongoingAutomation"
  | "features"
  | "workoutSettings"
  | "language"
> {
  language: AppLanguage;
  notificationsSettings: NotificationsSettings;
  availableOffers: AvailableOffer[];
  onboarding: TraineeOnboarding;
  config: TraineeConfig;
  ongoingAutomation: OngoingAutomation[];
  features: string[];
  workoutSettings?: null | WorkoutSettings;
}
